/**
 * Multiplayer API Service
 * Handles all multiplayer challenge operations
 */

import axios from 'axios';

const getDefaultApiUrl = () => {
  if (typeof window !== 'undefined' && window.location) {
    const hostname = window.location.hostname || 'localhost';
    const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
    const isPrivateLan =
      /^10\./.test(hostname) ||
      /^192\.168\./.test(hostname) ||
      /^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname);
    const protocol = (isLocal || isPrivateLan) ? 'http:' : (window.location.protocol || 'http:');
    const origin = window.location.origin || `${protocol}//${window.location.host}`;
    const port = window.location.port || '';
    const isDevFrontendPort = port === '3000' || port === '3001';
    if (isLocal) {
      return `${protocol}//${hostname}:5000/api`;
    }
    if (isPrivateLan && isDevFrontendPort) {
      return `${protocol}//${hostname}:5000/api`;
    }
    return `${origin.replace(/\/+$/, '')}/api`;
  }
  return 'http://localhost:5000/api';
};

const resolveApiUrl = () => {
  const defaultUrl = getDefaultApiUrl();
  const envUrl = process.env.REACT_APP_API_URL;

  if (envUrl && typeof window !== 'undefined' && window.location) {
    const pageHost = window.location.hostname;
    const pageIsLocal = pageHost === 'localhost' || pageHost === '127.0.0.1';
    const envIsLocal = /localhost|127\.0\.0\.1/i.test(envUrl);
    if (!pageIsLocal && envIsLocal) return defaultUrl;
  }

  return envUrl || defaultUrl;
};

const api = axios.create({
  baseURL: resolveApiUrl(),
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const multiplayerAPI = {
  /**
   * Create a new multiplayer challenge
   */
  createChallenge: (maxPlayers, wager, options = {}) => {
    return api.post('/multiplayer/create', {
      maxPlayers,
      wager,
      gameType: 'free-for-all',
      ...options
    });
  },

  /**
   * Get available multiplayer challenges
   */
  getAvailableChallenges: (params = {}) => {
    return api.get('/multiplayer/available', { params });
  },

  /**
   * Get the user's active multiplayer challenge
   */
  getActiveChallenge: (params = {}) => {
    return api.get('/multiplayer/active', { params });
  },

  /**
   * Get all active multiplayer challenges for the user
   */
  getActiveChallenges: (params = {}) => {
    return api.get('/multiplayer/active-list', { params });
  },

  /**
   * Get a specific multiplayer challenge by ID
   */
  getChallenge: (challengeId) => {
    return api.get(`/multiplayer/${challengeId}`);
  },

  /**
   * Join an existing multiplayer challenge
   */
  joinChallenge: (challengeId) => {
    return api.post(`/multiplayer/join/${challengeId}`);
  },

  /**
   * Start a multiplayer challenge (creator only)
   */
  startChallenge: (challengeId) => {
    return api.post(`/multiplayer/start/${challengeId}`);
  },

  /**
   * Mark current player as ready (required before game start)
   */
  setReady: (challengeId) => {
    return api.post(`/multiplayer/ready/${challengeId}`);
  },

  /**
   * Cancel a multiplayer challenge (creator only)
   */
  cancelChallenge: (challengeId) => {
    return api.post(`/multiplayer/cancel/${challengeId}`);
  },

  /**
   * Complete multiplayer game with scores
   */
  completeChallenge: (challengeId, playerScores) => {
    return api.post(`/multiplayer/complete/${challengeId}`, {
      playerScores
    });
  },

  /**
   * Submit a dice roll for multiplayer challenge
   */
  submitRoll: (challengeId) => {
    // Dice are generated server-side; client only requests to roll.
    return api.post(`/multiplayer/roll/${challengeId}`);
  },

  /**
   * Get all rolls for a multiplayer challenge
   */
  getRolls: (challengeId) => {
    return api.get(`/multiplayer/rolls/${challengeId}`);
  },

  /**
   * Get multiplayer game history
   */
  getGameHistory: (userId) => {
    return api.get(`/multiplayer/history/${userId}`);
  }
};

export default multiplayerAPI;
